import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Pause, CheckCircle2, Clock, Activity, AlertCircle, RotateCcw, XCircle, Trash2 } from 'lucide-react';
import VitalsDisplay from '@/components/VitalsDisplay';

const InfusionMonitor = ({ open, onClose, infusion, onUpdate }) => {
  const [session, setSession] = useState(infusion);
  const [elapsed, setElapsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showTimeAdjust, setShowTimeAdjust] = useState(false);
  const [timeAdjustment, setTimeAdjustment] = useState({ minutes: 0, seconds: 0 });
  const [vitalsData, setVitalsData] = useState({
    ta_systolique: '',
    ta_diastolique: '',
    pouls: '',
    temperature: '',
    spo2: ''
  });
  const [eventData, setEventData] = useState({
    type: 'info',
    description: ''
  });

  useEffect(() => {
    setSession(infusion);
    setIsPaused(infusion?.is_paused || false);
  }, [infusion]);

  useEffect(() => {
    if (session && session.statut === 'en_cours' && session.start_time && !isPaused) {
      const interval = setInterval(() => {
        const start = new Date(session.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000); // seconds
        setElapsed(diff);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [session, isPaused]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = async () => {
    try {
      await axios.put(`${API}/infusions/${session.id}/start`);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      onUpdate();
    } catch (error) {
      console.error('Error starting infusion:', error);
      alert('Erreur lors du démarrage');
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir terminer cette séance ?')) {
      return;
    }

    try {
      await axios.put(`${API}/infusions/${session.id}/complete`);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error completing infusion:', error);
      alert('Erreur lors de la clôture');
    }
  };

  const handlePause = async () => {
    try {
      await axios.patch(`${API}/infusions/${session.id}/pause`);
      setIsPaused(true);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      onUpdate();
    } catch (error) {
      console.error('Error pausing infusion:', error);
      alert('Erreur lors de la mise en pause');
    }
  };

  const handleResume = async () => {
    try {
      await axios.patch(`${API}/infusions/${session.id}/resume`);
      setIsPaused(false);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      onUpdate();
    } catch (error) {
      console.error('Error resuming infusion:', error);
      alert('Erreur lors de la reprise');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser cette perfusion ? Le chronométre sera remis à zéro.')) {
      return;
    }

    try {
      await axios.patch(`${API}/infusions/${session.id}/reset`);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      setElapsed(0);
      setIsPaused(false);
      onUpdate();
    } catch (error) {
      console.error('Error resetting infusion:', error);
      alert('Erreur lors de la réinitialisation');
    }
  };

  const handleAdjustTime = async () => {
    const totalSeconds = (timeAdjustment.minutes * 60) + timeAdjustment.seconds;
    
    try {
      await axios.patch(`${API}/infusions/${session.id}/adjust-time`, {
        seconds: totalSeconds
      });
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      setShowTimeAdjust(false);
      setTimeAdjustment({ minutes: 0, seconds: 0 });
      onUpdate();
    } catch (error) {
      console.error('Error adjusting time:', error);
      alert('Erreur lors de l\'ajustement du temps');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette perfusion ?')) {
      return;
    }

    try {
      await axios.patch(`${API}/infusions/${session.id}/cancel`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error canceling infusion:', error);
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement cette perfusion ? Cette action est irréversible.')) {
      return;
    }

    try {
      await axios.delete(`${API}/infusions/${session.id}`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting infusion:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAddVitals = async () => {
    try {
      const vitals = {};
      // Convertir avec le bon type selon le champ
      const intFields = ['ta_systolique', 'ta_diastolique', 'pouls', 'spo2'];
      const floatFields = ['temperature'];
      
      Object.keys(vitalsData).forEach(key => {
        if (vitalsData[key] !== '' && vitalsData[key] !== null) {
          if (intFields.includes(key)) {
            vitals[key] = parseInt(vitalsData[key], 10);
          } else if (floatFields.includes(key)) {
            vitals[key] = parseFloat(vitalsData[key]);
          } else {
            vitals[key] = parseFloat(vitalsData[key]);
          }
        }
      });

      await axios.post(`${API}/infusions/${session.id}/vitals`, vitals);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      setShowVitalsForm(false);
      setVitalsData({
        ta_systolique: '',
        ta_diastolique: '',
        pouls: '',
        temperature: '',
        spo2: ''
      });
      onUpdate();
    } catch (error) {
      console.error('Error adding vitals:', error);
      alert('Erreur lors de l\'ajout des constantes');
    }
  };

  const handleAddEvent = async () => {
    if (!eventData.description) {
      alert('Veuillez saisir une description');
      return;
    }

    try {
      await axios.post(`${API}/infusions/${session.id}/events`, eventData);
      const updated = await axios.get(`${API}/infusions/${session.id}`);
      setSession(updated.data);
      setShowEventForm(false);
      setEventData({ type: 'info', description: '' });
      onUpdate();
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Erreur lors de l\'ajout de l\'événement');
    }
  };

  const getStatusColor = (statut) => {
    const colors = {
      planifie: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-yellow-100 text-yellow-700',
      termine: 'bg-green-100 text-green-700',
      annule: 'bg-red-100 text-red-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getEventIcon = (type) => {
    if (type === 'warning' || type === 'incident') return <AlertCircle className="w-4 h-4 text-orange-500" />;
    return <Activity className="w-4 h-4 text-blue-500" />;
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="infusion-monitor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Monitoring Perfusion IV</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(session.statut)}`}>
              {session.statut}
            </span>
          </DialogTitle>
          <DialogDescription>
            Protocole: {session.protocole.nom} • Durée prévue: {session.protocole.duree} min
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Timer et contrôles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  Chronomètre
                  {isPaused && (
                    <span className="ml-3 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      EN PAUSE
                    </span>
                  )}
                </span>
                {session.statut === 'en_cours' && (
                  <span className="text-3xl font-mono font-bold text-blue-600">
                    {formatTime(elapsed)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Main controls */}
                <div className="flex space-x-3">
                  {session.statut === 'planifie' && (
                    <Button
                      onClick={handleStart}
                      className="flex-1"
                      data-testid="start-infusion-button"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white'
                      }}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Démarrer la séance
                    </Button>
                  )}
                  {session.statut === 'en_cours' && (
                    <>
                      {!isPaused ? (
                        <Button
                          onClick={handlePause}
                          variant="outline"
                          className="flex-1"
                          data-testid="pause-infusion-button"
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </Button>
                      ) : (
                        <Button
                          onClick={handleResume}
                          className="flex-1"
                          data-testid="resume-infusion-button"
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white'
                          }}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Reprendre
                        </Button>
                      )}
                      <Button
                        onClick={handleComplete}
                        className="flex-1"
                        data-testid="complete-infusion-button"
                        style={{
                          background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
                          color: 'white'
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Terminer
                      </Button>
                    </>
                  )}
                </div>

                {/* Secondary controls */}
                {session.statut !== 'termine' && (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTimeAdjust(true)}
                      data-testid="adjust-time-button"
                    >
                      Ajuster le temps
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      data-testid="reset-infusion-button"
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      className="hover:bg-orange-50 hover:text-orange-600"
                      data-testid="cancel-infusion-button"
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      className="hover:bg-red-50 hover:text-red-600"
                      data-testid="delete-infusion-button"
                    >
                      Supprimer
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bouton Vue Patient */}
          {session.statut === 'en_cours' && (
            <Button
              onClick={() => {
                const patientViewUrl = `/patient-view/${session.id}`;
                window.open(patientViewUrl, 'PatientView', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
              }}
              className="w-full py-6 text-lg"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white'
              }}
              data-testid="patient-view-button"
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Ouvrir Vue Patient (Écran de détente)
            </Button>
          )}

          {/* Actions rapides */}
          {session.statut === 'en_cours' && (
            <div className="grid grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowVitalsForm(true)}>
                <CardContent className="pt-6 text-center">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium">Ajouter Constantes</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowEventForm(true)}>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                  <p className="font-medium">Signaler Événement</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Vitals Timeline */}
          {session.vitals_timeline && session.vitals_timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Constantes enregistrées ({session.vitals_timeline.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {session.vitals_timeline.slice().reverse().map((entry, idx) => (
                    <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-sm text-gray-500 mb-2">
                        {new Date(entry.timestamp).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <VitalsDisplay vitals={entry} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Events */}
          {session.events && session.events.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Journal d'événements ({session.events.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {session.events.slice().reverse().map((event, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      {getEventIcon(event.type)}
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{event.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(event.timestamp).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Vitals Form Dialog */}
        {showVitalsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4">
              <CardHeader>
                <CardTitle>Ajouter Constantes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>TA Systolique (mmHg)</Label>
                    <Input
                      type="number"
                      value={vitalsData.ta_systolique}
                      onChange={(e) => setVitalsData({ ...vitalsData, ta_systolique: e.target.value })}
                      data-testid="vital-ta-systolique"
                    />
                  </div>
                  <div>
                    <Label>TA Diastolique (mmHg)</Label>
                    <Input
                      type="number"
                      value={vitalsData.ta_diastolique}
                      onChange={(e) => setVitalsData({ ...vitalsData, ta_diastolique: e.target.value })}
                      data-testid="vital-ta-diastolique"
                    />
                  </div>
                  <div>
                    <Label>Pouls (bpm)</Label>
                    <Input
                      type="number"
                      value={vitalsData.pouls}
                      onChange={(e) => setVitalsData({ ...vitalsData, pouls: e.target.value })}
                      data-testid="vital-pouls"
                    />
                  </div>
                  <div>
                    <Label>Température (°C)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={vitalsData.temperature}
                      onChange={(e) => setVitalsData({ ...vitalsData, temperature: e.target.value })}
                      data-testid="vital-temperature"
                    />
                  </div>
                  <div>
                    <Label>SpO₂ (%)</Label>
                    <Input
                      type="number"
                      value={vitalsData.spo2}
                      onChange={(e) => setVitalsData({ ...vitalsData, spo2: e.target.value })}
                      data-testid="vital-spo2"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setShowVitalsForm(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleAddVitals} data-testid="save-vitals-button">Enregistrer</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Event Form Dialog */}
        {showEventForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl m-4">
              <CardHeader>
                <CardTitle>Signaler un Événement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Type</Label>
                  <select
                    value={eventData.type}
                    onChange={(e) => setEventData({ ...eventData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="info">Information</option>
                    <option value="warning">Avertissement</option>
                    <option value="incident">Incident</option>
                  </select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={eventData.description}
                    onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                    rows={4}
                    placeholder="Décrivez l'événement..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setShowEventForm(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleAddEvent}>Enregistrer</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Time Adjustment Dialog */}
        {showTimeAdjust && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md m-4">
              <CardHeader>
                <CardTitle>Ajuster le Temps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Ajoutez ou retirez du temps au chronomètre. Utilisez des valeurs positives pour avancer le temps, négatives pour le reculer.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Minutes</Label>
                    <Input
                      type="number"
                      value={timeAdjustment.minutes}
                      onChange={(e) => setTimeAdjustment({ ...timeAdjustment, minutes: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Secondes</Label>
                    <Input
                      type="number"
                      value={timeAdjustment.seconds}
                      onChange={(e) => setTimeAdjustment({ ...timeAdjustment, seconds: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
                  <p className="text-blue-800">
                    Ajustement: <strong>{(timeAdjustment.minutes * 60) + timeAdjustment.seconds} secondes</strong>
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => {
                    setShowTimeAdjust(false);
                    setTimeAdjustment({ minutes: 0, seconds: 0 });
                  }}>
                    Annuler
                  </Button>
                  <Button onClick={handleAdjustTime}>Appliquer</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InfusionMonitor;
